import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimationOnScroll } from 'react-animation-on-scroll';
import * as ReactUI from 'react-bootstrap';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  activeNavLinkIndexState,
  activeTabState,
  backgroundSizeState,
  backgroundThemeState,
  darkModeState,
  navItemHoverState,
  navItemIdsState,
} from '../../atoms';
import { connect } from '../../data';
import * as Icon from '../../data/photoUrls';
import { BGSize, changeBackground, loadFontAndBorderColor } from '../../util';
import { useActiveState, useHover } from '../../util/hooks';
import { SideBarInfo, sideBarInfo } from './SideBarInfo';
import './styles.scss';

const navigationState: Record<string, number> = {
  home: 0,
  skills: 1,
  projects: 2,
  contact: 3,
};

const NavBar = () => {
  const darkMode = useRecoilValue(darkModeState);
  const [activeTab, setActiveTab] = useRecoilState(activeTabState);
  const [navItemColors, setNavItemColors] = useRecoilState(navItemHoverState);
  const [navItemIds, setNavItemIds] = useRecoilState(navItemIdsState);

  const [activeIndex, setActiveIndex] = useState(activeTab);
  const [background, setBackground] = useRecoilState(backgroundThemeState);
  const [backgroundSize, setBackgroundSize] =
    useRecoilState(backgroundSizeState);

  const [navItemIndexActive, setNavItemIndexActive] = useRecoilState(
    activeNavLinkIndexState
  );
  const themeState = !darkMode ? 'row-container' : 'row-container-dark';
  const [activeBackground] = useActiveState(
    sideBarInfo(background),
    activeIndex
  );
  const { hover } = useHover();

  const handleClick = useCallback(
    (tabName: string, bg: string, bgSize: BGSize, e: any, bgColor: string) => {
      e.preventDefault();
      setActiveTab(tabName);
      setActiveIndex(tabName);
      setNavItemIndexActive(navigationState[tabName]);
      activeBackground(e.target.id, bgColor);
      setBackground(bg);
      setBackgroundSize(bgSize);
      changeBackground(themeState, bg, bgSize);
    },
    [
      activeBackground,
      setActiveTab,
      setBackground,
      setBackgroundSize,
      setNavItemIndexActive,
      themeState,
    ]
  );

  const className = useMemo(
    () => (item: SideBarInfo) => (!darkMode ? item.id : item.idDark),
    [darkMode]
  );

  useEffect(() => {
    changeBackground(themeState, background, backgroundSize);

    loadFontAndBorderColor(
      navItemIds,
      navItemColors,
      setNavItemColors,
      setNavItemIds
    );

    activeBackground(
      sideBarInfo(background)[navItemIndexActive].id,
      sideBarInfo(background)[navItemIndexActive].bgColor!
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background]);

  return (
    <div id={!darkMode ? 'navbar' : 'navbarDark'}>
      <ReactUI.Nav className="light">
        <ReactUI.Nav.Item>
          <a
            style={{
              textDecoration: 'none',
              color: 'inherit',
              alignSelf: 'center',
            }}
            href="#home"
          >
            <ReactUI.NavbarBrand
              style={{ padding: '10px 15px', marginBottom: '40px' }}
              className="d-flex align-items-center brand-container"
            >
              <AnimationOnScroll className="animate__fadeInUp">
                <div>
                  <ReactUI.Image
                    className="brand"
                    src={
                      !darkMode ? Icon.portfolioIcon : Icon.portfolioIconDark
                    }
                    alt=""
                    width="28px"
                  />
                </div>
              </AnimationOnScroll>
              <AnimationOnScroll className="animate__fadeInDownBig">
                <div className="portfolio">
                  <span className="my-span">My </span>
                  <br />
                  <span className="portfolio-span">Portfolio</span>
                </div>
              </AnimationOnScroll>
            </ReactUI.NavbarBrand>
          </a>

          {sideBarInfo(background).map((item) => {
            return (
              <ReactUI.Navbar.Toggle
                key={item.title}
                className={`${item.className}`}
              >
                <AnimationOnScroll className="animate__fadeInLeftBig">
                  <a
                    href={item.path}
                    onClick={(event) =>
                      handleClick(
                        item.path,
                        !darkMode ? item.bgLight : item.bg,
                        item.bgSize,
                        event,
                        item.bgColor!
                      )
                    }
                    onMouseOver={() => hover(item)}
                  >
                    <div id={item.id} className={className(item)}>
                      {item.title}
                    </div>
                  </a>
                </AnimationOnScroll>
              </ReactUI.Navbar.Toggle>
            );
          })}
        </ReactUI.Nav.Item>

        <div style={{ position: 'fixed', bottom: '50px', padding: '20px' }}>
          <AnimationOnScroll className="animate__fadeInUp">
            <div className="d-flex">
              <div style={{ marginRight: '5px' }}>♾</div>
              {connect.map((icon) => (
                <a key={icon.link} href={icon.link}>
                  <div style={{ marginRight: '10px' }}>
                    <ReactUI.Image
                      src={!darkMode ? icon.light : icon.dark}
                      alt=""
                      width="27px"
                    />
                  </div>
                </a>
              ))}
            </div>
          </AnimationOnScroll>
        </div>

        <div style={{ position: 'fixed', bottom: '0px', padding: '20px' }}>
          <AnimationOnScroll className="animate__fadeInUpBig">
            <p>{` © Ejiroghene Egbedi ${new Date().getFullYear()}`}</p>
          </AnimationOnScroll>
        </div>
      </ReactUI.Nav>
    </div>
  );
};

export default NavBar;
